import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Landing from './components/Landing';
import Login from './components/Login';
import Signup from './components/Signup';
import Dashboard from './components/Dashboard';
import TopUp from './components/TopUp';
import Community from './components/Community';
import JapanLife from './components/JapanLife';
import KenyaConnect from './components/KenyaConnect';
import { PLANS, MOCK_ACTIVITIES } from './data/constants';
import { post } from './data/api';

export const AppContext = createContext(null);

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');
  return ctx;
}

function PrivateRoute({ children }) {
  const { token } = useApp();
  return token ? children : <Navigate to="/login" replace />;
}

function InstallBanner() {
  const [prompt,  setPrompt]  = useState(null);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (localStorage.getItem('pwa_dismissed')) return;

    function handlePrompt(e) {
      e.preventDefault();
      setPrompt(e);
      if (window.innerWidth < 768) {
        timerRef.current = setTimeout(() => setVisible(true), 30000);
      }
    }

    window.addEventListener('beforeinstallprompt', handlePrompt);
    return () => {
      window.removeEventListener('beforeinstallprompt', handlePrompt);
      clearTimeout(timerRef.current);
    };
  }, []);

  function handleInstall() {
    if (!prompt) return;
    prompt.prompt();
    prompt.userChoice.then(() => setVisible(false));
  }

  function handleDismiss() {
    localStorage.setItem('pwa_dismissed', '1');
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
      background: '#111827', color: '#fff',
      padding: '14px 16px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      boxShadow: '0 -4px 20px rgba(0,0,0,.3)',
    }}>
      <div style={{ fontSize: 14, lineHeight: 1.4 }}>
        <strong style={{ color: '#4ade80' }}>mudiaspora</strong>
        <span style={{ color: 'rgba(255,255,255,.8)' }}> — Install on your phone for faster access</span>
      </div>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <button
          onClick={handleInstall}
          style={{
            background: '#16a34a', color: '#fff', border: 'none',
            borderRadius: 8, padding: '8px 16px', fontSize: 13,
            fontWeight: 700, cursor: 'pointer',
          }}
        >
          Install
        </button>
        <button
          onClick={handleDismiss}
          style={{
            background: 'transparent', color: 'rgba(255,255,255,.6)',
            border: '1px solid rgba(255,255,255,.2)',
            borderRadius: 8, padding: '8px 12px', fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Not now
        </button>
      </div>
    </div>
  );
}

export default function App() {
  const [token,      setToken]      = useState(() => localStorage.getItem('md_token') || null);
  const [user,       setUser]       = useState(() => {
    try { return JSON.parse(localStorage.getItem('md_user')) || null; }
    catch { return null; }
  });

  const [credits,    setCredits]    = useState(user?.credits ?? 0);
  const [plan,       setPlanState]  = useState(() => PLANS.find(p => p.id === (user?.plan || 'starter')) || PLANS[0]);
  const [properties, setProperties] = useState(user?.properties || []);
  const [activities, setActivities] = useState(MOCK_ACTIVITIES);

  useEffect(() => {
    setCredits(user?.credits ?? 0);
    setPlanState(PLANS.find(p => p.id === (user?.plan || 'starter')) || PLANS[0]);
    setProperties(user?.properties || []);
  }, [user]);

  const login = useCallback((data) => {
    localStorage.setItem('md_token', data.token);
    localStorage.setItem('md_user',  JSON.stringify(data));
    setToken(data.token);
    setUser(data);
  }, []);

  const logout = useCallback(async () => {
    try { await post('logout', { token }); } catch {}
    localStorage.removeItem('md_token');
    localStorage.removeItem('md_user');
    setToken(null);
    setUser(null);
    setActivities(MOCK_ACTIVITIES);
  }, [token]);

  function setPlan(plan) {
    setPlanState(plan);
    const updated = { ...user, plan: plan.id, credits: plan.credits, max_credits: plan.credits };
    setUser(updated);
    localStorage.setItem('md_user', JSON.stringify(updated));
  }

  function addCredits(amount) {
    setCredits(prev => Math.round((prev + amount) * 10) / 10);
  }

  function deductCredits(amount) {
    setCredits(prev => Math.round(Math.max(0, prev - amount) * 10) / 10);
  }

  function addActivity(entry) {
    setActivities(prev => [
      { id: Date.now(), date: new Date().toISOString().slice(0, 10), status: 'pending', ...entry },
      ...prev,
    ]);
  }

  const value = {
    token, user,
    credits, plan, properties, activities,
    login, logout,
    addCredits, deductCredits, addActivity,
    setPlan, setProperties,
  };

  return (
    <AppContext.Provider value={value}>
      <InstallBanner />
      <BrowserRouter>
        <Routes>
          <Route path="/"          element={<Landing />} />
          <Route path="/community" element={<Community />} />
          <Route path="/japan-life" element={<JapanLife />} />
          <Route path="/kenya-connect" element={<KenyaConnect />} />
          <Route path="/login"     element={<Login />} />
          <Route path="/signup"    element={<Signup />} />
          <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/topup"     element={<PrivateRoute><TopUp /></PrivateRoute>} />
          <Route path="*"          element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AppContext.Provider>
  );
}
